'use client';

import type { ReactElement } from 'react';
import Image from 'next/image';
import { Link2Off, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { DeleteConfirmPopover } from '@/components/ui/delete-confirm-popover';
import { toast } from '@/lib/toast';
import {
  usePmsMatterContactBindings,
  useUnbindPmsMatterContact,
  usePmsStatus
} from '@/lib/services/integrations/pms/hooks';
import { getPmsProviderPresentationFor } from '@/lib/services/integrations/pms/provider-presentation';
import { pmsBindErrorCopy } from '@/lib/services/integrations/pms/bind-error-copy';
import { getPmsContactBindingDetailState } from '@/lib/services/integrations/pms/contact-binding-state';
import { cn } from '@/lib/utils';

/**
 * The PMS binding state for one contact on one matter, plus the explicit **unbind** action.
 *
 * ## Why unbinding is manual, and must stay that way
 *
 * Vale does not reconcile the association with the PMS. Changing a contact's type files them under
 * the new one and deliberately leaves the old link in place, and a contact removed from the matter
 * *in the PMS* keeps reading as bound here until someone clears it. That is the accepted trade for
 * never double-handling the association — so this control is the cleanup path, and nothing should
 * ever be wired to perform it automatically.
 *
 * ## Rendering
 *
 * Renders nothing at all unless the contact actually has a stored binding on this matter, so a
 * firm on no PMS — and any contact with nothing filed — sees no affordance. The type label
 * comes from the stored row rather than the provider's catalogue, so this displays correctly even
 * when the PMS is unreachable. That is also what lets it serve a **derived** binding, whose provider
 * publishes no catalogue to look anything up in (VALE-160).
 *
 * ## Read-only for a derived binding
 *
 * Everything above is the *display* half, and it is provider-agnostic. The **Unbind** half is not: a
 * `derived` row records a link the provider made from its own role assignment, its provider
 * implements no removal API, and `PmsContactBindingService` refuses the call — so the affordance is
 * withheld rather than offered and left to 400. See `canUnbind` below.
 *
 * **`matterId` and `contactId` are required, so the caller must gate on them** — mounting this at all
 * costs a PMS status query, and the surfaces that reuse the contact form with no matter behind them
 * (the global contacts list, and the AI-review cards, which stage *proposed* contacts) must not pay
 * it. `RoleSection` owns that gate.
 */
export function PmsContactBindingRow({
  matterId,
  contactId,
  contactName,
  showProvider = true,
  hasTypeRowAbove = false,
  className
}: {
  matterId: number;
  contactId: number;
  /** Used in the confirm copy, so the question names what is being unbound. */
  contactName?: string;
  /**
   * Whether to lead with the provider's mark and name.
   *
   * `false` where the **surrounding chrome already names the provider** — the
   * Contacts drawer's attribute path (VALE-158) heads this row's whole section
   * with that provider's mark and label, so repeating them one line below is
   * the same attribution twice on a surface whose argument is density.
   *
   * Defaults to `true`: on the contact card this row stands alone in the Role
   * section with nothing else naming the PMS, so it must attribute itself.
   */
  showProvider?: boolean;
  /**
   * Whether a `Participant type` row is rendered directly above this one, already
   * showing the stored type.
   *
   * Drives the redundancy collapse below — see {@link isRedundantFiledRow}. Only
   * the attribute path ever passes `true`, and only when a catalogue exists to
   * populate that row: a Smokeball section has no such row (VALE-160), so its
   * `Synced with … as` badge is the only place the type appears and must survive.
   *
   * Defaults to `false` — the honest answer for the contact card, where this row
   * stands alone in the Role section.
   */
  hasTypeRowAbove?: boolean;
  className?: string;
}): ReactElement | null {
  const { data: status } = usePmsStatus();
  const provider = status?.provider ?? undefined;

  const { data: bindings } = usePmsMatterContactBindings(
    provider,
    matterId,
    !!status?.isEnabled
  );
  const unbind = useUnbindPmsMatterContact(provider, matterId);

  const binding = bindings?.find((entry) => entry.contactId === contactId);

  // No row means nothing is filed — there is no default and no fallback — so there is nothing to
  // show and nothing to unbind.
  if (!provider || !binding) {
    return null;
  }

  const presentation = getPmsProviderPresentationFor(provider);
  /*
    The one collapse of `isBound` / `lastError` into a state, shared with the
    contacts listing's badge (VALE-159) rather than re-derived here — three
    independent readings of the same two fields is how surfaces end up
    disagreeing about what "not yet filed" looks like.
  */
  const state = getPmsContactBindingDetailState(binding);

  /**
   * **A cleanly-filed row says nothing the surface above it hasn't.**
   *
   * On the attribute path the `Participant type` row already renders the stored
   * type, so `Synced with … as [Executor]` directly beneath it is the same fact twice —
   * and on a treatment whose entire argument is density, a restatement is worse
   * than an omission. Only the **Unbind** affordance survives, which is the one
   * thing that row uniquely offers.
   *
   * **Scoped to there actually being such a row, not to the path** (VALE-160).
   * It keyed on `!showProvider` while the attribute path always rendered the
   * selector above — true until a provider with no catalogue could produce a
   * binding. A Smokeball section has no `Participant type` row, so keying on the
   * path would collapse the badge as redundant against something that is not
   * there, leaving a provider-headed section with nothing in it at all.
   *
   * On the contact card the selector renders **nothing in read mode**, so there
   * the badge is likewise the only place the type appears — which the `false`
   * default already says.
   *
   * Every other state still shows: `Syncing with … as` is an operation in progress, and
   * both error states carry information the row above cannot.
   */
  const isRedundantFiledRow = hasTypeRowAbove && state === 'filed';

  /**
   * Only a binding the user actually chose can be unbound (VALE-160).
   *
   * A `derived` row records a link the provider made from its own role assignment,
   * and those providers implement no removal API — the endpoint refuses the call.
   * See the affordance's own comment below.
   */
  const canUnbind = binding.origin !== 'derived';

  const handleUnbind = async (): Promise<void> => {
    try {
      const { unlinkedInPms } = await unbind.mutateAsync(contactId);

      toast.success(
        unlinkedInPms
          ? `Removed from the matter in ${presentation.label}`
          : `Cleared the ${presentation.label} contact type`
      );
    } catch (error) {
      // The stored binding is left untouched by a failed unbind, so the contact correctly still
      // reads as bound after this — the user can retry. `DeleteConfirmPopover` has no error surface
      // of its own by design; reporting the failure is the caller's job.
      toast.error(
        error instanceof Error
          ? error.message
          : `Could not remove the contact from the matter in ${presentation.label}`
      );
    }
  };

  return (
    <div
      className={cn('text-13px flex flex-wrap items-center gap-2', className)}
    >
      {showProvider && !!presentation.logoUrl && (
        <Image
          src={presentation.logoUrl}
          alt=""
          width={16}
          height={16}
          className="shrink-0 rounded-xs"
        />
      )}

      {showProvider && (
        <span className="text-muted-foreground">{presentation.label}</span>
      )}

      {/*
        With the provider suppressed, the badge loses its lead-in — and a bare
        type badge sitting directly under a `Participant type` row showing the
        same word reads as the row repeated rather than as its outcome. The
        unbound states already say what they are ("Not yet filed", the error),
        so only the bound ones need naming.

        **Both bound states, not just the erroring one** (VALE-160). A plain
        `filed` row on this path used to be suppressed outright as redundant, so
        it could never reach here — the only bound badge that survived was
        `filed-with-error`, and the condition was written to match. A section
        with no `Participant type` row above it (Smokeball) renders the plain
        `filed` badge instead of collapsing it, and a bare `[Beneficiary]` under
        the provider's heading states a type without saying it is filed. Keyed
        off {@link isRedundantFiledRow} rather than repeating its terms, so the
        lead-in and the collapse cannot drift apart.
      */}
      {!showProvider &&
        !isRedundantFiledRow &&
        (state === 'filed' || state === 'filed-with-error') && (
          /*
            **`Synced with <provider> as`, not `Filed as`** (VALE-863). "Filed"
            reads as though Vale created the contact in the provider, which is
            not what this row records — it records that a Vale contact and a
            provider contact are the same person, under that provider's own
            type. Tom's 27 Aug review named the implication directly and asked
            for the relationship stated plainly.

            **Past tense, not the imperative.** *"Sync with Smokeball as
            Beneficiary"* reads as a button — an instruction to do something —
            when the row is reporting a state that already holds. `Synced`
            says the same thing as a fact, and pairs with the in-flight
            `Syncing with … as` below it, so the two states read as one
            sentence in two tenses.

            **The provider comes from `presentation.label`, never a literal.**
            This row also serves ActionStep, and a hardcoded "Smokeball" would
            be wrong there — the same restatement trap the rest of this file
            avoids by reading the provider's own presentation.
          */
          <span className="text-12px text-muted-foreground">
            Synced with {presentation.label} as
          </span>
        )}

      {/*
        **Before the badge, not after it** — the state is a lead-in, exactly as
        `Synced with … as` is, so the two read as one pattern rather than two:
        `Synced with Smokeball as [Executor]` / `Syncing with Smokeball as
        [Executor]`. Trailing it after the
        badge (as the approved design's own frame happened to show) makes the
        same words read as a footnote to the type instead of the type's status.
      */}
      {state === 'in-flight' && (
        <span className="text-12-5px text-muted-foreground">
          Syncing with {presentation.label} as
        </span>
      )}

      {!isRedundantFiledRow && (
        <StatusBadge
          tone={
            state === 'filed'
              ? 'success'
              : state === 'filed-with-error'
                ? 'critical'
                : state === 'failed'
                  ? 'critical'
                  : 'neutral'
          }
          emphasis="subtle"
        >
          {binding.pmsContactTypeLabel}
        </StatusBadge>
      )}

      {/*
        **The in-flight state is genuinely in flight, so it spins.** `Syncing with … as`
        is not a resting state: the selection is stored and the bind has not
        landed, and the bindings query is actively polling for the outcome
        (`pmsContactBindingsPollInterval`). A static line would read as a
        settled status the reader has to guess the freshness of, where a spinner
        says the answer is coming.
        `aria-hidden` because the adjacent `Syncing with … as` already carries the state
        for a screen reader — the spinner is the visual half of the same fact,
        not a second one.
      */}
      {state === 'in-flight' && (
        <Loader2
          className="text-foreground-subtle h-3.5 w-3.5 shrink-0 animate-spin"
          aria-hidden
        />
      )}

      {/*
        Rendered only for `failed` — the contact was never filed, so this is
        the primary failure signal and gets the destructive styling. A row
        that IS filed but still carrying an error is a distinct state
        (`filed-with-error`, below) with its own, less alarming treatment,
        because the contact's standing on the matter is the fact that
        matters more there.

        `last_error` cannot be stale behind a later success: `recordBindSuccess`
        clears it in the same statement that sets `is_bound`, so a bound row
        carrying an error always reflects the most recent attempt, never a
        leftover from one a later re-bind superseded (VALE-291).

        Mapped, never the raw wire string (`docs/attribute-treatment.md` §2) —
        ActionStep's own words are "Error while trying to validate record",
        which names no cause and reads as an internal fault. The provider's
        string stays one hover away in `title` for support.
      */}
      {state === 'failed' && (
        <span
          className="text-12-5px text-destructive"
          title={binding.lastError!}
        >
          {pmsBindErrorCopy(binding.lastError)}
        </span>
      )}

      {/*
        Filed, and still carrying an error — shown rather than hidden, because
        the error is routinely the NEWER of the two writes (see
        `PmsContactBindingDetailState`). Muted rather than destructive: the
        contact IS on the matter, so this reports the last attempt, not the
        contact's standing.
      */}
      {state === 'filed-with-error' && (
        <span
          className="text-12-5px text-error-subtle-foreground"
          title={binding.lastError!}
        >
          {pmsBindErrorCopy(binding.lastError)}
        </span>
      )}

      {/*
        **A derived binding offers no unbind** (VALE-160). The provider filed this
        contact from its own role rather than from a type anyone chose, and the
        providers that do that implement no removal API — `PmsContactBindingService`
        refuses the call outright. Rendering the affordance would hand the reader a
        button that always 400s, and (worse) one whose only honest outcome would be
        deleting a row the next matter sync immediately re-derives.

        So a derived row is read-only: provider, type, and state. Removing such a
        contact is done in the PMS, or in Vale by taking them off the matter — a
        different mechanism (the `pms_contact_matter_unlinks` tombstone), not this
        button.
      */}
      {canUnbind && (
        <DeleteConfirmPopover
          trigger={
            <Button
              density="compact"
              tone="muted"
              padY="3xs"
              type="button"
              variant="ghost"
              /*
                `default`, not `sm` — `sm` is `h-9`, which at this theme's 3.4px
                `--spacing` is a **30.6px** control carrying 14px text, sitting in
                a 13px row. Measured against the approved design (12.5px text, ~21px
                tall) it out-weighed everything beside it, which is what made it
                read as a peer of the status rather than a quiet trailing action.
                `default` has no fixed height, so it takes its size from the row.
              */
              size="auto"
              disabled={unbind.isPending}
              aria-label={`Unbind this contact from the matter in ${presentation.label}`}
              data-testid="pms-unbind-trigger"
              /*
                **Pushed to the trailing edge, quiet, and sized to the text it
                sits beside.** The outcome (badge, and whatever qualifies it) is
                what the reader is here for; Unbind is the rarely-wanted
                destructive action, so it sits apart from the status rather than
                butting against it — the artifact design's treatment, and the
                same reasoning as §2's "destructive actions move to the header".
                Left inline with a default-sized icon it read as a peer of the
                badge, and its icon out-weighed the 13px row it belongs to.
              */
              /*
                The approved design's own metrics: 12.5px, slate-500, 2px/7px
                padding, pushed to the trailing edge. `size="default"` supplies
                the padding (3.4px/6.8px at this theme's `--spacing`); the rest is
                set here because `Button`'s base type scale is 14px, which is
                larger than the 13px row it sits in.
              */
              className="ml-auto [&_svg]:size-3.5"
            >
              <Link2Off aria-hidden="true" />
              Unbind
            </Button>
          }
          title={`Unbind ${contactName?.trim() || 'this contact'} from the matter in ${presentation.label}?`}
          description={
            binding.isBound
              ? `Removes them from the matter in ${presentation.label} and clears the "${binding.pmsContactTypeLabel}" contact type here. Choosing a type again will file them back.`
              : `Clears the "${binding.pmsContactTypeLabel}" contact type. They were never filed on the matter in ${presentation.label}, so nothing changes there.`
          }
          confirmLabel="Unbind"
          onConfirm={handleUnbind}
        />
      )}
    </div>
  );
}
